import { TFunction } from "i18next";

export const IMAGE_MARK = "[@]";
export const DISPLAYLINES_PREFIX = "\\begin{gather}";
export const DISPLAYLINES_SUFFIX = "\\end{gather}";

export type LocalImage = {
  name: string,
  blob: Blob,
  type: string,
  url?: string, // 可选的URL字段，用于直接显示图片
}

export type RichText = {
  text: string,
  backendImageFile: BackendImageFile[],
  localImages: LocalImage[],
}

export type BackendImageFile = {
  name: string,
  url?: string,
  type?: string,
}

export enum BackendQuestionFileJsonKind {
    QuestionImage,
    StandardAnswerImage,
    AnswerImage,
}

export type BackendQuestionFileJson = {
    questionImages: BackendImageFile[],
    standardAnswerImages: BackendImageFile[],
    answerImages: BackendImageFile[],
}

/**
 * 从 filesJson 中解析图片数据并转换为 LocalImage 数组
 * @param filesJsonStr - JSON 字符串，包含图片信息
 * @param imageType - 图片类型：'question' | 'standardAnswer' | 'answer'
 * @returns LocalImage 数组
 */
export function parseImagesFromFilesJson(
  filesJsonStr?: string,
  imageType: 'question' | 'standardAnswer' | 'answer' = 'question'
): LocalImage[] {
  if (!filesJsonStr) {
    return [];
  }

  try {
    const filesJson: BackendQuestionFileJson = JSON.parse(filesJsonStr);
    let imageArray: BackendImageFile[] = [];

    // 根据图片类型选择对应的图片数组
    switch (imageType) {
      case 'question':
        imageArray = filesJson.questionImages || [];
        break;
      case 'standardAnswer':
        imageArray = filesJson.standardAnswerImages || [];
        break;
      case 'answer':
        imageArray = filesJson.answerImages || [];
        break;
    }

    if (!imageArray || imageArray.length === 0) {
      return [];
    }

    // 根据文件类型推断扩展名的通用函数
    const getExtensionFromType = (type?: string): string => {
      const typeMap: { [key: string]: string } = {
        'image/jpeg': '.jpg',
        'image/jpg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/webp': '.webp',
        'image/bmp': '.bmp',
        'image/svg+xml': '.svg'
      };
      return typeMap[type || ''] || '.jpg';
    };

    return imageArray.map((file: BackendImageFile, index: number) => {
      const extension = file.name && file.name.includes('.') ?
        '.' + file.name.split('.').pop() : getExtensionFromType(file.type);

      // 根据图片类型生成名称前缀
      let namePrefix = '';
      switch (imageType) {
        case 'question':
          namePrefix = 'q-';
          break;
        case 'standardAnswer':
          namePrefix = 'a-';
          break;
        case 'answer':
          namePrefix = '';
          break;
      }

      return {
        name: file.name || `${namePrefix}${index + 1}${extension}`,
        blob: new Blob(), // 临时空blob，将在 ImageUpload 组件中异步加载
        type: file.type || 'image/jpeg',
        url: file.url
      };
    }).filter((img: LocalImage) => img.url) as LocalImage[]; // 过滤掉没有URL的图片
  } catch (error) {
    console.error('解析图片数据失败:', error);
    return [];
  }
}

/**
 * Process backslash sequences - convert all \\ to newlines
 * @param text Text containing backslashes
 * @returns Text with backslashes converted to newlines
 */
export function processBackslashes(text: string): string {
  if (!text) {
    return text;
  }

  let result = text;

  // 首先保护 LaTeX 的显示数学模式标记 \\[ 和 \\]
  // 将它们临时替换为占位符
  const PLACEHOLDER_START = '___LATEX_DISPLAY_START___';
  const PLACEHOLDER_END = '___LATEX_DISPLAY_END___';

  // 检查是否包含 LaTeX 显示数学标记
  const hasDisplayStart = result.includes('\\\\[');
  const hasDisplayEnd = result.includes('\\\\]');

  // 使用字符串替换，直接替换字符串序列
  if (hasDisplayStart) {
    result = result.replaceAll('\\\\[', PLACEHOLDER_START);
  }
  if (hasDisplayEnd) {
    result = result.replaceAll('\\\\]', PLACEHOLDER_END);
  }

  // Process backslash sequences:
  // \\ followed by any character (including space) -> newline + character
  // This converts \\$ to \n$, \\ (space) to \n (space), etc.
  result = result.replace(/\\\\(.)/g, '\n$1');

  // Also handle \\ at end of string (no following character)
  result = result.replace(/\\\\$/g, '\n');

  // 恢复 LaTeX 标记
  result = result.replace(new RegExp(PLACEHOLDER_START, 'g'), '\\[');
  result = result.replace(new RegExp(PLACEHOLDER_END, 'g'), '\\]');

  return result;
}

/**
 * Convert spaces and tabs to LaTeX space commands for math-field
 * @param text Text containing spaces and tabs
 * @returns Text with LaTeX space commands
 */
export function convertSpacesToLatex(text: string): string {
  if (!text) {
    return text;
  }

  // Split on existing LaTeX space commands to avoid double-converting them.
  // Tokens like \qquad, \quad, \:, \; are passed through unchanged;
  // only plain spaces in the segments between them are converted.
  const parts = text.split(/(\\qquad\s*|\\quad\s*|\\:\s*|\\;\s*|\\\\\s)/);

  const converted = parts.map((part, i) => {
    // Odd-indexed parts are the LaTeX space command matches — leave them alone
    if (i % 2 === 1) return part;

    let result = part;
    result = result.replace(/\t/g, '\\qquad ');
    result = result.replace(/ {4,}/g, '\\qquad ');
    result = result.replace(/ {2,3}/g, '\\quad ');
    result = result.replace(/ /g, '\\: ');
    return result;
  });

  return converted.join('');
}

/**
 * Normalize LaTeX math delimiters for remark-math compatibility.
 * Converts \(...\) → $...$ and \[...\] → $$...$$
 * so that remark-math (which only understands $ delimiters) can parse them.
 */
export function normalizeMathDelimiters(text: string): string {
  if (!text) return text;

  // Convert \[...\] to $$...$$ (display math) — must be done before inline
  // Use non-greedy match; handle multiline with [\s\S]
  text = text.replace(/\\\[([\s\S]*?)\\\]/g, (_match, inner) => `$$${inner}$$`);

  // Convert \(...\) to $...$ (inline math)
  text = text.replace(/\\\(([\s\S]*?)\\\)/g, (_match, inner) => `$${inner}$`);

  return text;
}

/** Convert stored \n line separators to LaTeX \\ line breaks for use inside \begin{gather} */
export function newlinesToLatexBreaks(text: string): string {
  if (!text) return text;
  return text.replace(/\n/g, '\\\\ ');
}

export function removeDisplaylines(answer: string): string {
    let index = answer.indexOf(DISPLAYLINES_PREFIX);
    if (index != -1) {
        answer = answer.replaceAll(DISPLAYLINES_PREFIX, "");
        answer = answer.replaceAll(DISPLAYLINES_SUFFIX, "");
        answer = answer.replaceAll("\\begin{multline}", "");
        answer = answer.replaceAll("\\end{multline}", "");
        return answer;
    }
    return answer;
}

export function wrapDisplaylines(answer?: string): string {
    if (!answer) {
        return DISPLAYLINES_PREFIX + DISPLAYLINES_SUFFIX;
    } else {
        return DISPLAYLINES_PREFIX + answer + DISPLAYLINES_SUFFIX
    }
}

export function processImage(t: TFunction, isReadOnly: boolean, kind: BackendQuestionFileJsonKind, json?: string, text?: string): RichText {
  if (!text) {
    return { text: '', backendImageFile: [], localImages: [] };
  }

  let index = text.indexOf(IMAGE_MARK);
  let nextIndex = text.indexOf(IMAGE_MARK, index + 1);
  let backendImageFile: BackendImageFile[] = [];

  if (json) {
    let jsonObj: BackendQuestionFileJson = JSON.parse(json);
    switch (kind) {
        case BackendQuestionFileJsonKind.AnswerImage:
            backendImageFile = jsonObj.answerImages;
            break;
        case BackendQuestionFileJsonKind.QuestionImage:
            backendImageFile = jsonObj.questionImages;
            break;
        case BackendQuestionFileJsonKind.StandardAnswerImage:
            backendImageFile = jsonObj.standardAnswerImages;
            break;
    }
  }

  if (isReadOnly) {
    while (index > 0 && nextIndex > 0) {
      let imagePath = text.substring(index + IMAGE_MARK.length, nextIndex);
      let imageName = imagePath;
      if (imageName.indexOf('/') !== -1) {
        imageName = imageName.substring(imageName.lastIndexOf('/'));
      }

      text = text.replace(IMAGE_MARK + imagePath + IMAGE_MARK, '（' + t('component.img') + imageName + '）');

      index = text.indexOf(IMAGE_MARK);
      nextIndex = text.indexOf(IMAGE_MARK, index + 1);
    }
  }

  const result = {
    backendImageFile: backendImageFile,
    text: text,
    localImages: [],
  };
  return result;
}

export function processHandWriting(content?: string): string | undefined {
    if (!content) {
        return;
    }

    try {
        const base64 = JSON.parse(content).handwriting as string;
        return base64;
    } catch (error: any) {
        console.log('json error', error.message);
    }

    return undefined;
}
